import React, { useState, useRef, useEffect } from 'react';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Bot, User, Trash2, Plus, Loader2 } from 'lucide-react';

const DauterAI = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    toast.success('Started new chat');
  };

  const handleClearHistory = async () => {
    if (!sessionId || !user) return;
    
    try {
      await axios.delete(`${API}/chat/history/${sessionId}`, { withCredentials: true });
      setMessages([]);
      setSessionId(null);
      toast.success('Chat history cleared');
    } catch (error) {
      toast.error('Failed to clear history');
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: input,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: input, session_id: sessionId },
        { withCredentials: true }
      );

      const aiMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
      };

      setMessages((prev) => [...prev, aiMessage]);
      setSessionId(response.data.session_id);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to get response. Please try again.');
      setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dauter-ai';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="dauter-ai-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-sm mb-4">
            <Bot className="w-5 h-5 text-red-500" />
            <span className="text-sm font-semibold text-red-500 uppercase tracking-wide">AI Assistant</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mb-2">
            Dauter <span className="text-red-500">AI</span>
          </h1>
          <p className="text-zinc-400">Your intelligent study companion powered by GPT-5.2</p>
        </div>

        {/* Chat Container */}
        <div className="chat-container rounded-sm overflow-hidden">
          {/* Toolbar */}
          <div className="bg-[#0A0A0A] border-b border-red-500/20 px-4 py-3 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-sm text-zinc-400">Online</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="text-zinc-400 hover:text-white"
                data-testid="new-chat-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Chat
              </Button>
              {user && sessionId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearHistory}
                  className="text-zinc-400 hover:text-red-500"
                  data-testid="clear-history-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="h-[500px] p-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Bot className="w-16 h-16 text-red-500/50 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">How can I help you today?</h3>
                <p className="text-zinc-500 max-w-md">
                  Ask me anything about your studies - homework help, exam prep, 
                  concept explanations, or career guidance.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[
                    'Explain photosynthesis',
                    'Help with math homework',
                    'Career options after 12th',
                    'Tips for exam preparation',
                  ].map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(prompt)}
                      className="px-4 py-2 text-sm text-zinc-400 border border-zinc-800 rounded-sm hover:border-red-500/50 hover:text-white transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-sm bg-red-500/20 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-5 h-5 text-red-500" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-sm ${
                        message.role === 'user'
                          ? 'chat-message-user'
                          : 'chat-message-ai'
                      }`}
                    >
                      <p className="text-zinc-300 whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 rounded-sm bg-zinc-700 flex items-center justify-center flex-shrink-0">
                        {user?.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-sm" />
                        ) : (
                          <User className="w-5 h-5 text-zinc-400" />
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-sm bg-red-500/20 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="chat-message-ai p-4 rounded-sm">
                      <Loader2 className="w-5 h-5 text-red-500 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input */}
          <div className="bg-[#0A0A0A] border-t border-red-500/20 p-4">
            {user ? (
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="bg-[#121212] border-zinc-800 focus:border-red-500 text-white placeholder:text-zinc-500"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 rounded-sm"
                  data-testid="send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-zinc-400 mb-3">Sign in to chat with Dauter AI</p>
                <Button
                  onClick={handleGoogleLogin}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                  data-testid="signin-to-chat-btn"
                >
                  Sign In to Continue
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 text-center text-sm text-zinc-500">
          <p>Chat history is saved to your account and linked to your email.</p>
        </div>
      </div>
    </div>
  );
};

export default DauterAI;
